import React, { useState } from 'react';
import {
  Tooltip,
  Box,
  Typography,
  Divider,
  Button,
  Modal,
  Paper,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { ITherapy } from 'interfaces/redux/ITherapy';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles(theme => ({
  tooltipContent: {
    padding: '12px',
    fontSize: '13px',
    lineHeight: '1.6',
  },
  medicationItem: {
    'marginBottom': '8px',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  divider: {
    margin: '8px 0',
    backgroundColor: '#ffffff',
    opacity: 0.8,
  },
  moreIndicator: {
    marginTop: '8px',
    fontStyle: 'italic',
    fontSize: '12px',
  },
  modalPaper: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '80vh',
    padding: '24px',
    overflow: 'auto',
    boxShadow: theme.palette.mode === 'light' ? theme.shadows[5] : theme.shadows[20],
    borderRadius: '8px',
  },
  modalTitle: {
    marginBottom: '16px',
    fontWeight: 700,
    fontSize: '22px',
  },
  medicationsList: {
    maxHeight: '60vh',
    overflow: 'auto',
    marginBottom: '16px',
  },
  medicationItemInModal: {
    'paddingLeft': 0,
    'paddingRight': 0,
    'marginBottom': '12px',
    'paddingBottom': '0',
    '&:last-child': {
      marginBottom: 0,
      paddingBottom: 0,
    },
  },
  medicationName: {
    fontWeight: 500,
    fontSize: '14px',
    marginBottom: '4px',
  },
  medicationDetails: {
    fontSize: '12px',
    color: theme.palette.text.secondary,
  },
  medicationDetailText: {
    fontSize: '14px',
    lineHeight: '1.5',
  },
  closeButton: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '16px',
  },
  closeButtonStyled: {
    'textTransform': 'uppercase',
    'fontSize': '14px',
    'fontWeight': 500,
    'color': theme.palette.text.primary,
    'padding': '8px 16px',
    'border': '1px solid transparent',
    'backgroundColor': 'transparent',
    'borderRadius': '4px',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      border: `1px solid ${theme.palette.divider}`,
    },
  },
}));

interface MedicationInfo {
  drug_name: string;
  strength: string;
  strength_unit_of_measure: string;
  fill_cycle_number: number;
  administration_status: string;
  diagnosis_code?: string;
  start_date?: string;
  last_csl_completed_dt?: string;
  prescribing_physician_name?: string;
  dosis_regimen?: string;
  days_supply?: number;
  therapy_id: number; // For unique key
}

interface MedicationsTooltipProps {
  medicationsList: MedicationInfo[];
  maxDisplayInTooltip?: number;
  cursor?: 'pointer' | 'default';
  showIndex?: boolean;
  children: React.ReactNode;
}

const MAX_DISPLAY_DEFAULT = 4;

function formatDate(value?: string): string {
  if (!value) {
    return '-';
  }

  const isoDate = value.split('T')[0];
  const [year, month, day] = isoDate.split('-');
  if (!year || !month || !day) {
    return value;
  }

  return `${month}/${day}/${year}`;
}

function medicationTitle(med: MedicationInfo, index?: number, showIndex?: boolean): string {
  const strength = med.strength ? `${med.strength}${med.strength_unit_of_measure}` : '';
  const fullName = `${med.drug_name}${strength ? ` ${strength}` : ''}`;
  const indexPrefix = index !== undefined && showIndex !== false ? `${index + 1}. ` : '';
  const fcSuffix = med.fill_cycle_number ? ` (FC# ${med.fill_cycle_number})` : '';
  return `${indexPrefix}${fullName}${fcSuffix}`;
}

function medicationDetailBlock(
  med: MedicationInfo,
  index?: number,
  showIndex?: boolean,
  classes?: any,
): React.ReactElement {
  const detailSx = classes
    ? { mb: 0.25, fontSize: '14px', lineHeight: '1.5' }
    : { mb: 0.25, fontSize: '14px', lineHeight: '1.5' };
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, fontSize: '15px' }}>
        {medicationTitle(med, index, showIndex)}
      </Typography>
      <Typography variant="body2" display="block" sx={detailSx}>
        <span style={{ fontWeight: 700 }}>ICD-10:</span> {med.diagnosis_code || '-'}
      </Typography>
      <Typography variant="body2" display="block" sx={detailSx}>
        <span style={{ fontWeight: 700 }}>Start Date:</span> {formatDate(med.start_date)}
      </Typography>
      <Typography variant="body2" display="block" sx={detailSx}>
        <span style={{ fontWeight: 700 }}>Last CSL Completed Date:</span>{' '}
        {formatDate(med.last_csl_completed_dt)}
      </Typography>
      <Typography variant="body2" display="block" sx={detailSx}>
        <span style={{ fontWeight: 700 }}>Dosing Regimen:</span> {med.dosis_regimen || '-'}
      </Typography>
      <Typography variant="body2" display="block" sx={detailSx}>
        <span style={{ fontWeight: 700 }}>Prescriber:</span> {med.prescribing_physician_name || '-'}
      </Typography>
    </Box>
  );
}

export const MedicationsTooltip: React.FC<MedicationsTooltipProps> = ({
  medicationsList,
  maxDisplayInTooltip = MAX_DISPLAY_DEFAULT,
  cursor = 'pointer',
  showIndex = true,
  children,
}) => {
  const classes = useStyles();
  const [modalOpen, setModalOpen] = useState(false);

  if (!children) {
    return null;
  }

  if (!medicationsList || medicationsList.length === 0) {
    return <>{children}</>;
  }

  const displayedMeds = medicationsList.slice(0, maxDisplayInTooltip);
  const hiddenCount = medicationsList.length - maxDisplayInTooltip;
  const hasMoreMeds = hiddenCount > 0;

  const tooltipContent = (
    <Box className={classes.tooltipContent}>
      {displayedMeds.map((med, idx) => (
        <Box key={`${med.therapy_id}-${idx}`} className={classes.medicationItem}>
          {medicationDetailBlock(med, showIndex ? idx : undefined, showIndex)}
          {idx < displayedMeds.length - 1 ? <Divider className={classes.divider} /> : null}
        </Box>
      ))}
      {hasMoreMeds && (
        <>
          <Divider className={classes.divider} />
          <Box className={classes.moreIndicator}>+{hiddenCount} - Click Icon for more detaills</Box>
        </>
      )}
    </Box>
  );

  return (
    <>
      <Tooltip title={tooltipContent} placement="top">
        <Box component="div" onClick={() => setModalOpen(true)} style={{ cursor }}>
          {children}
        </Box>
      </Tooltip>

      <MedicationsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        medicationsList={medicationsList}
        showIndex={showIndex}
      />
    </>
  );
};

interface MedicationsModalProps {
  open: boolean;
  onClose: () => void;
  medicationsList: MedicationInfo[];
  showIndex?: boolean;
}

const MedicationsModal: React.FC<MedicationsModalProps> = ({
  open,
  onClose,
  medicationsList,
  showIndex,
}) => {
  const classes = useStyles();

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="medications-modal-title"
      aria-describedby="medications-modal-description"
    >
      <Paper className={classes.modalPaper}>
        <Typography id="medications-modal-title" className={classes.modalTitle}>
          Dosing Regimen FC Order ({medicationsList.length})
        </Typography>
        <Divider />

        <Box id="medications-modal-description" className={classes.medicationsList}>
          <List disablePadding>
            {medicationsList.map((med, idx) => (
              <ListItem
                key={`${med.therapy_id}-${idx}`}
                disableGutters
                className={classes.medicationItemInModal}
              >
                <ListItemText
                  primary={medicationDetailBlock(med, showIndex ? idx : undefined, showIndex)}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box className={classes.closeButton}>
          <Button className={classes.closeButtonStyled} onClick={onClose}>
            CLOSE
          </Button>
        </Box>
      </Paper>
    </Modal>
  );
};

export default MedicationsTooltip;
